import * as THREE from 'three';
import { LampGeometry } from 'lamp-core';
import { defaultRenderingConfig, thumbnailRenderingConfig } from '../config/renderingConfig.js';

// Camera configuration for thumbnail generation
const THUMBNAIL_CAMERA_CONFIG = {
  fov: 30,
  minDistance: 150,
  far: 2000
};

/**
 * Generates a thumbnail image for a lamp design
 * @param {Object} design - The lamp design object containing baseShape, modifiers, noiseSeed, etc.
 * @param {Object} options - Optional configuration overrides
 * @param {number} options.maxDimension - Maximum dimension for the thumbnail (default: 512)
 * @param {string} options.format - Image format ('image/jpeg' or 'image/png', default: 'image/jpeg')
 * @param {number} options.quality - Image quality for JPEG (0-1, default: 0.92)
 * @param {boolean} options.includeWireframe - Whether to include wireframe overlay (default: false)
 * @param {boolean} options.useAdvancedMaterial - Whether to use advanced material properties (default: true)
 * @param {boolean} options.enableDebugLogging - Whether to enable debug logging (default: false)
 * @returns {Promise<string>} Promise that resolves to the thumbnail data URL
 */
export async function generateLampThumbnail(design, options = {}) {
  const {
    maxDimension = 512,
    format = 'image/jpeg',
    quality = 0.92,
    includeWireframe = false,
    useAdvancedMaterial = true,
    enableDebugLogging = false
  } = options;

  if (!design) {
    throw new Error('Design object is required for thumbnail generation');
  }

  try {
    // Validate and normalize baseShape first
    if (!design.baseShape) {
      throw new Error('Design baseShape is required for thumbnail generation');
    }
    
    // Calculate default values if missing
    const lampHeight = design.baseShape.height || 250;
    const lampDiameter = design.baseShape.diameter || 100;
    const TARGET_FACE_SIZE = 5; // Same as in LampBuilder
    
    // Normalize baseShape with defaults if needed
    const normalizedBaseShape = {
      diameter: design.baseShape.diameter || lampDiameter,
      height: design.baseShape.height || lampHeight,
      tubularSegments: design.baseShape.tubularSegments || Math.max(4, Math.round(lampHeight / TARGET_FACE_SIZE)),
      radialSegments: design.baseShape.radialSegments || Math.max(8, Math.round((Math.PI * lampDiameter) / TARGET_FACE_SIZE)),
      profilePoints: design.baseShape.profilePoints || [
        { x: 1.0, y: 0.0 },
        { x: 1.0, y: 0.9 }
      ]
    };
    
    // Calculate dimensions based on lamp proportions (use normalized values)
    const aspect = normalizedBaseShape.height / normalizedBaseShape.diameter;
    
    let previewWidth, previewHeight;
    if (aspect >= 1) {
      // Tall lamp: height is max dimension
      previewHeight = maxDimension;
      previewWidth = Math.round(maxDimension / aspect);
    } else {
      // Wide lamp: width is max dimension
      previewWidth = maxDimension;
      previewHeight = Math.round(maxDimension * aspect);
    }

    // Create canvas and renderer
    const canvas = document.createElement('canvas');
    canvas.width = previewWidth;
    canvas.height = previewHeight;
    
    const renderer = new THREE.WebGLRenderer({ 
      canvas, 
      antialias: true, 
      powerPreference: 'high-performance', 
      preserveDrawingBuffer: true 
    });
    
    renderer.setSize(previewWidth, previewHeight);
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.setClearColor(
      thumbnailRenderingConfig.background?.color || defaultRenderingConfig.background.color, 
      1
    );

    // Create scene and camera
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      THUMBNAIL_CAMERA_CONFIG.fov,
      previewWidth / previewHeight,
      THUMBNAIL_CAMERA_CONFIG.minDistance,
      THUMBNAIL_CAMERA_CONFIG.far
    );
    
    // Position camera to fit the lamp nicely
    const fitHeight = normalizedBaseShape.height * 1.2;
    const fitDiameter = normalizedBaseShape.diameter * 1.2;
    const fit = Math.max(fitHeight, fitDiameter);
    camera.position.set(0, normalizedBaseShape.height / 2, fit * 2);
    camera.lookAt(0, normalizedBaseShape.height / 2, 0);

    // Setup lighting using configuration values
    const ambientLight = new THREE.AmbientLight(
      defaultRenderingConfig.lighting.ambientColor, 
      defaultRenderingConfig.lighting.ambientIntensity || 0.7
    );
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(
      defaultRenderingConfig.lighting.directionalColor, 
      defaultRenderingConfig.lighting.directionalIntensity || 1.2
    );
    directionalLight.position.set(
      defaultRenderingConfig.lighting.directionalPosition.x / 10, 
      defaultRenderingConfig.lighting.directionalPosition.y / 10, 
      defaultRenderingConfig.lighting.directionalPosition.z / 10
    );
    scene.add(directionalLight);
    
    // Add fill and rim lights for better appearance
    const fillLight = new THREE.DirectionalLight(defaultRenderingConfig.lighting.directionalColor, 0.5);
    fillLight.position.set(-2, 1, 1);
    scene.add(fillLight);
    
    const rimLight = new THREE.DirectionalLight(defaultRenderingConfig.lighting.directionalColor, 0.4);
    rimLight.position.set(0, 2, -2);
    scene.add(rimLight);
    
    // Create lamp geometry
    let geometry;
    try {
      geometry = new LampGeometry(
        normalizedBaseShape,
        design.modifiers || [],
        design.noiseSeed
      );
    } catch (error) {
      throw new Error(`Failed to create LampGeometry: ${error.message}`);
    }
    
    const threeGeometry = geometry.getGeometry();

    // Create material
    let materialConfig;
    if (useAdvancedMaterial) {
      materialConfig = {
        color: design.rendering?.material?.color || defaultRenderingConfig.material.color,
        roughness: design.rendering?.material?.roughness ?? defaultRenderingConfig.material.roughness,
        metalness: design.rendering?.material?.metalness ?? defaultRenderingConfig.material.metalness,
        opacity: defaultRenderingConfig.material.opacity,
        clearcoat: 0.6,
        clearcoatRoughness: 0.1,
        reflectivity: 0.7,
        side: THREE.DoubleSide
      };
    } else {
      materialConfig = {
        color: design.rendering?.material?.color || defaultRenderingConfig.material.color,
        roughness: design.rendering?.material?.roughness ?? defaultRenderingConfig.material.roughness,
        metalness: design.rendering?.material?.metalness ?? defaultRenderingConfig.material.metalness,
        opacity: defaultRenderingConfig.material.opacity,
        side: THREE.DoubleSide
      };
    }
    
    const material = new THREE.MeshPhysicalMaterial(materialConfig);

    // Create and add mesh
    const mesh = new THREE.Mesh(threeGeometry, material);
    scene.add(mesh);
    
    // Add wireframe overlay if requested
    if (includeWireframe && defaultRenderingConfig.view.wireframe) {
      const wireframeMaterial = new THREE.MeshBasicMaterial({
        color: defaultRenderingConfig.view.wireframeColor || 0x000000,
        wireframe: true,
        transparent: true,
        opacity: 0.8
      });
      const wireframeMesh = new THREE.Mesh(threeGeometry, wireframeMaterial);
      wireframeMesh.position.copy(mesh.position);
      wireframeMesh.position.z += 0.001; // Slight offset to avoid z-fighting
      scene.add(wireframeMesh);
    }

    // Render the scene
    renderer.render(scene, camera);

    // Generate data URL
    const dataURL = canvas.toDataURL(format, format === 'image/jpeg' ? quality : undefined);

    // Clean up resources (wrap in try-catch to prevent cleanup errors from breaking the function)
    try {
      // Collect all meshes first, then dispose them (don't modify scene during traversal)
      const meshesToDispose = [];
      if (scene && typeof scene.traverse === 'function') {
        try {
          scene.traverse((object) => {
            if (object && object.isMesh) {
              meshesToDispose.push(object);
            }
          });
        } catch (traverseError) {
          // Ignore traversal errors during cleanup
        }
      }
      
      // Dispose meshes after traversal
      meshesToDispose.forEach((mesh) => {
        try {
          if (mesh.geometry && typeof mesh.geometry.dispose === 'function') {
            mesh.geometry.dispose();
          }
          if (mesh.material) {
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach(mat => {
                if (mat && typeof mat.dispose === 'function') {
                  mat.dispose();
                }
              });
            } else if (typeof mesh.material.dispose === 'function') {
              mesh.material.dispose();
            }
          }
        } catch (meshError) {
          // Ignore mesh disposal errors during cleanup
        }
      });
      
      // Clear scene children
      if (scene && Array.isArray(scene.children)) {
        while (scene.children.length > 0) {
          scene.remove(scene.children[0]);
        }
      }
      
      if (geometry && typeof geometry.dispose === 'function') {
        geometry.dispose();
      }
      if (renderer && typeof renderer.dispose === 'function') {
        renderer.dispose();
      }
    } catch (cleanupError) {
      // Ignore cleanup errors - they shouldn't break thumbnail generation
    }
    
    return dataURL;
    
  } catch (error) {
    throw new Error(`Failed to generate thumbnail: ${error.message}`);
  }
}

/**
 * Generates and caches a thumbnail for a lamp design
 * @param {Object} design - The lamp design object
 * @param {Object} options - Optional configuration overrides
 * @returns {Promise<string>} Promise that resolves to the thumbnail data URL
 */
export async function generateCachedLampThumbnail(design, options = {}) {
  if (!design || !design.id) {
    throw new Error('Design object with ID is required for cached thumbnail generation');
  }

  const cacheKey = `lamp_thumbnail_${design.id}`;
  
  // Check cache first
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return cached;
    }
  } catch (e) {
    // Ignore cache read errors
  }
  
  // Generate new thumbnail
  const thumbnail = await generateLampThumbnail(design, options);
  
  // Cache the result
  try {
    localStorage.setItem(cacheKey, thumbnail);
  } catch (e) {
    // Ignore cache write errors (e.g., quota exceeded)
  }
  
  return thumbnail;
}

/**
 * Batch generates thumbnails for multiple designs
 * @param {Array} designs - Array of lamp design objects
 * @param {Object} options - Optional configuration overrides
 * @returns {Promise<Array>} Promise that resolves to array of designs with preview property
 */
export async function generateBatchThumbnails(designs, options = {}) {
  if (!Array.isArray(designs)) {
    throw new Error('Designs must be an array');
  }

  const designsWithPreviews = await Promise.all(designs.map(async (design) => {
    try {
      const preview = await generateCachedLampThumbnail(design, options);
      return {
        ...design,
        preview
      };
    } catch (error) {
      return {
        ...design,
        preview: null
      };
    }
  }));
  
  return designsWithPreviews;
}